import { useCallback } from 'react';

import { useReCaptcha } from '@/hooks/useReCaptcha';

export type MintRecaptchaToken = (action: string) => Promise<string | undefined>;

/**
 * Mints a reCAPTCHA v3 token for a submission, resolving `undefined` instead of
 * throwing when one cannot be obtained (no site key configured, script blocked,
 * Google unreachable).
 *
 * The fail-open decision lives server-side: cms-integration-service verifies the
 * token and decides whether a missing one is fatal (RECAPTCHA_ENFORCED). Blocking
 * the member here would make a Google outage a registration outage twice over.
 */
export const useRecaptchaMint = (screen: string): MintRecaptchaToken => {
  const { executeReCaptcha, isReCaptchaEnabled } = useReCaptcha({ screen });

  return useCallback(
    async (action: string): Promise<string | undefined> => {
      if (!isReCaptchaEnabled) return undefined;
      try {
        return await executeReCaptcha(action);
      } catch {
        return undefined;
      }
    },
    [executeReCaptcha, isReCaptchaEnabled],
  );
};
